// Vendor record foundations — right-side edit drawer for the vendor
// contact profile.
//
// FIND-12 discipline: EVERY editable field hydrates from the vendor row
// when the drawer opens, and Save always sends the full current form state
// — so "open → save unchanged" round-trips the same values and a blank
// optional field can never silently wipe one the form didn't show.

import { useEffect, useState } from "react";
import { Button, FormField, Input, Textarea } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useUpdateVendorProfile } from "@/hooks/portfolioAdmin/useRegistries";
import { ResourceDrawer } from "../loanDetail/common/ResourceDrawer";
import { VendorTypeOptGroups, VENDOR_TYPE_OPTIONS } from "./VendorsListFilters";
import type { VendorDto } from "@/api/portfolioAdmin/types";

interface VendorEditDrawerProps {
  vendor: VendorDto;
  open: boolean;
  onClose: () => void;
}

export function VendorEditDrawer({ vendor, open, onClose }: VendorEditDrawerProps) {
  const toast = useToast();
  const update = useUpdateVendorProfile(vendor.id);

  const [name, setName] = useState(vendor.name);
  const [vendorType, setVendorType] = useState(vendor.vendorType ?? "");
  const [contactName, setContactName] = useState(vendor.contactName ?? "");
  const [email, setEmail] = useState(vendor.email ?? "");
  const [phone, setPhone] = useState(vendor.phone ?? "");
  const [address, setAddress] = useState(vendor.address ?? "");
  const [notes, setNotes] = useState(vendor.notes ?? "");
  const [billingTermsNotes, setBillingTermsNotes] = useState(
    vendor.billingTermsNotes ?? "",
  );
  const [isRegulated, setIsRegulated] = useState(vendor.isRegulated ?? false);

  // Hydrate from the row every time the drawer opens (never a blank form).
  useEffect(() => {
    if (!open) return;
    setName(vendor.name);
    setVendorType(vendor.vendorType ?? "");
    setContactName(vendor.contactName ?? "");
    setEmail(vendor.email ?? "");
    setPhone(vendor.phone ?? "");
    setAddress(vendor.address ?? "");
    setNotes(vendor.notes ?? "");
    setBillingTermsNotes(vendor.billingTermsNotes ?? "");
    setIsRegulated(vendor.isRegulated ?? false);
  }, [open, vendor]);

  // FIND-12: a stored legacy free-text type that isn't a catalog code gets
  // its own "(current value)" option so open → save round-trips it
  // unchanged instead of silently rewriting the data.
  const storedType = vendor.vendorType ?? "";
  const storedTypeIsLegacy =
    storedType !== "" &&
    !VENDOR_TYPE_OPTIONS.some((o) => o.value === storedType);

  const onSave = async () => {
    if (!name.trim()) {
      toast.error("Vendor name is required.");
      return;
    }
    try {
      await update.mutateAsync({
        name: name.trim(),
        vendorType,
        contactName,
        email,
        phone,
        address,
        notes,
        billingTermsNotes,
        isRegulated,
      });
      toast.success("Vendor updated.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      title={vendor.name}
      subtitle="Edit vendor details"
      testId="vendor-edit-drawer"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void onSave()}
            disabled={update.isPending || !name.trim()}
            data-testid="vendor-edit-save"
          >
            {update.isPending ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-space-3">
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="vendor-edit-name"
          />
        </FormField>

        <FormField label="Type">
          <select
            className="w-full px-space-2 py-[6px] border border-border-light rounded-sm bg-white-pure text-small"
            value={vendorType}
            onChange={(e) => setVendorType(e.target.value)}
            data-testid="vendor-edit-type"
          >
            <option value="">Not set</option>
            <VendorTypeOptGroups />
            {storedTypeIsLegacy ? (
              <option value={storedType}>{storedType} (current value)</option>
            ) : null}
          </select>
        </FormField>

        <FormField label="Contact name" helperText="The portal invitee.">
          <Input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            data-testid="vendor-edit-contact-name"
          />
        </FormField>

        <FormField
          label="Email"
          helperText="Required before a portal invitation can be sent."
        >
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="vendor-edit-email"
          />
        </FormField>

        <FormField label="Phone">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="vendor-edit-phone"
          />
        </FormField>

        <FormField label="Address">
          <Textarea
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={2}
            data-testid="vendor-edit-address"
          />
        </FormField>

        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            data-testid="vendor-edit-notes"
          />
        </FormField>

        <FormField
          label="Billing terms"
          helperText="Shown on the VFI Accounting audit drawer when reviewing this vendor's draft invoices."
        >
          <Textarea
            value={billingTermsNotes}
            onChange={(e) => setBillingTermsNotes(e.target.value)}
            rows={3}
            data-testid="vendor-edit-billing-terms"
          />
        </FormField>

        <label className="flex items-start gap-space-2 text-small">
          <input
            type="checkbox"
            className="accent-gold mt-space-1"
            checked={isRegulated}
            onChange={(e) => setIsRegulated(e.target.checked)}
            data-testid="vendor-edit-regulated"
          />
          <span>
            Regulated vendor (borrower-paid)
            <span className="block text-caption text-gray-mid">
              Regulated vendors bill the borrower directly through Phoenix:
              their monthly report lists the fees for services, and accepting
              it drafts an invoice to the borrower&apos;s portal — never the
              vendor&apos;s.
            </span>
          </span>
        </label>
      </div>
    </ResourceDrawer>
  );
}
